/**
 * Streaming API Client
 *
 * Uses Server-Sent Events (SSE) to receive tokens as they stream
 * from the backend, enabling immediate rendering and reduced
 * perceived latency.
 */

import type { OrchestrateRequest, MessageBlock } from '../config';
import type { ToolRequest, ToolResult } from '../api-executor';
import { debug } from '../logger';

/** Abort SSE stream if no data received for this duration (ms) */
const LIVENESS_TIMEOUT_MS = 45_000;

// =============================================================================
// TYPES
// =============================================================================

export interface StreamCallbacks {
  /** Called when stream starts with session ID */
  onStart?: (sessionId: string) => void;
  /** Called for each token received */
  onToken?: (token: string) => void;
  /** Called when a complete UI block is received */
  onBlock?: (block: MessageBlock) => void;
  /** Called when Claude requests a tool execution */
  onToolRequest?: (request: ToolRequest) => Promise<ToolResult>;
  /** Called when more blocks are being generated (shows loading indicator) */
  onLoading?: () => void;
  /** Called when backend sends the resolved language code */
  onLanguage?: (language: string) => void;
  /** Called when stream completes */
  onDone?: (inputTokens: number, outputTokens: number) => void;
  /** Called on error */
  onError?: (error: string) => void;
}

// =============================================================================
// STREAMING CLIENT CLASS
// =============================================================================

export class StreamingClient {
  private baseUrl: string;
  private apiKey: string | null = null;
  private abortController: AbortController | null = null;
  /** Current session ID for tool result submissions */
  private currentSessionId: string | null = null;
  /** Track pending tool result streams for cleanup */
  private pendingToolAbortController: AbortController | null = null;
  /** Flag to indicate if stream was explicitly aborted (not just ended normally) */
  private isAborted = false;
  /** Flag to indicate if stream was aborted due to liveness timeout */
  private isLivenessTimeout = false;
  /** Counter for pending tool operations (tool execution + result submission) */
  private pendingToolOperations = 0;
  /** Sequential tool execution queue - prevents race conditions in comparison mode */
  private toolQueue: Array<{ request: ToolRequest; callbacks: StreamCallbacks }> = [];
  /** Whether a tool is currently being processed */
  private isProcessingTool = false;
  /** Deferred done callback — held until all tool operations complete */
  private deferredDone: { callbacks: StreamCallbacks; inputTokens: number; outputTokens: number } | null = null;
  /** Resolves when all tool operations complete — lets stream() await tool completion */
  private toolsCompleteResolver: (() => void) | null = null;
  private toolsCompletePromise: Promise<void> | null = null;
  /** Liveness timer — aborts if no SSE data received within threshold */
  private livenessTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl.replace(/\/$/, '');
    // MOTHBALLED: Keep reference to suppress TS6133 for mothballed method
    void this._enqueueToolRequest;
  }

  /**
   * Reset the liveness timer. Called on every SSE chunk received.
   * If no data arrives within LIVENESS_TIMEOUT_MS, the stream is considered dead.
   */
  private resetLivenessTimer(): void {
    this.clearLivenessTimer();
    this.livenessTimer = setTimeout(() => {
      debug('StreamingClient: liveness timeout — no data for', LIVENESS_TIMEOUT_MS / 1000, 's');
      this.isLivenessTimeout = true;
      this.abortController?.abort();
    }, LIVENESS_TIMEOUT_MS);
  }

  private clearLivenessTimer(): void {
    if (this.livenessTimer !== null) {
      clearTimeout(this.livenessTimer);
      this.livenessTimer = null;
    }
  }

  /**
   * Set API key for authenticated requests.
   */
  setApiKey(apiKey: string | null): void {
    this.apiKey = apiKey;
  }

  /**
   * Stream an orchestration request.
   *
   * @param workspaceId The workspace to chat with
   * @param sessionId The session ID for conversation continuity
   * @param message The user's message
   * @param callbacks Callbacks for stream events
   * @param speedMode Optional flag for speed mode (concise responses, minimal UI blocks)
   * @param language Optional language code for multilingual AI responses
   */
  async stream(
    workspaceId: string,
    sessionId: string,
    message: string,
    callbacks: StreamCallbacks,
    speedMode?: boolean,
    language?: string,
    visitorAuthenticated?: boolean
  ): Promise<void> {
    // Cancel any existing stream (including tool result streams)
    this.abort();
    this.abortController = new AbortController();
    this.isAborted = false;
    this.isLivenessTimeout = false;

    const request: OrchestrateRequest = {
      workspaceId,
      sessionId,
      message,
      speedMode,
      language,
      visitorAuthenticated,
      currentPageUrl: cleanPageUrl(window.location.href),
    };

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Accept': 'text/event-stream',
    };

    if (this.apiKey) {
      headers['X-Api-Key'] = this.apiKey;
    }

    let reader: ReadableStreamDefaultReader<Uint8Array> | null = null;

    // Create timeout for connection (30 seconds)
    const timeoutId = setTimeout(() => {
      debug('StreamingClient: connection timeout after 30s');
      this.abortController?.abort();
    }, 30000);

    try {
      debug('StreamingClient: starting fetch to', `${this.baseUrl}/api/orchestrate/stream`);
      const response = await fetch(`${this.baseUrl}/api/orchestrate/stream`, {
        method: 'POST',
        headers,
        body: JSON.stringify(request),
        signal: this.abortController.signal,
      });

      // Clear connection timeout once response starts
      clearTimeout(timeoutId);
      debug('StreamingClient: received response', response.status);

      if (!response.ok) {
        const error = await response.text();
        debug('StreamingClient: response not ok', response.status, error);
        // Try to parse structured error responses (usage limit, etc.)
        try {
          const parsed = JSON.parse(error);
          if (parsed.code === 'USAGE_LIMIT_EXCEEDED') {
            callbacks.onError?.(`USAGE_LIMIT:${JSON.stringify({
              limit: parsed.details?.limit,
              current: parsed.details?.current,
              message: parsed.message
            })}`);
            return;
          }
        } catch { /* not JSON, fall through */ }
        callbacks.onError?.(error || `HTTP ${response.status}`);
        return;
      }

      reader = response.body?.getReader() ?? null;
      if (!reader) {
        callbacks.onError?.('No response body');
        return;
      }

      const decoder = new TextDecoder();
      let buffer = '';

      // Start liveness timer — if no data arrives within threshold, abort
      this.resetLivenessTimer();

      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          break;
        }

        // Data received — connection is alive, reset timer
        this.resetLivenessTimer();

        buffer += decoder.decode(value, { stream: true });

        // Process complete events in buffer
        const events = buffer.split('\n\n');
        buffer = events.pop() || '';

        for (const eventText of events) {
          this.processEvent(eventText, callbacks);
        }
      }

      // Process any remaining buffer
      if (buffer.trim()) {
        this.processEvent(buffer, callbacks);
      }

    } catch (error) {
      debug('StreamingClient: caught error', error);
      if (error instanceof Error && error.name === 'AbortError') {
        if (this.isLivenessTimeout) {
          debug('StreamingClient: liveness timeout — connection died mid-stream');
          callbacks.onError?.('Connection lost. Please try again.');
        } else if (!this.isAborted) {
          debug('StreamingClient: connection timed out');
          callbacks.onError?.('Connection timed out. Please try again.');
        } else {
          debug('StreamingClient: intentionally aborted');
        }
        return;
      }
      // If tool operations are pending or a deferred done is queued, the stream
      // delivered its payload successfully before the connection broke (common
      // with HTTP/2 protocol errors after SSE completes). Let the tool pipeline
      // finish and fire onDone — don't surface a spurious network error.
      if (this.pendingToolOperations > 0 || this.toolQueue.length > 0 || this.deferredDone) {
        debug('StreamingClient: suppressing network error — tool operations will complete',
          'pending:', this.pendingToolOperations, 'queued:', this.toolQueue.length,
          'deferredDone:', !!this.deferredDone);
        return;
      }
      debug('StreamingClient: calling onError with', error instanceof Error ? error.message : 'Stream error');
      callbacks.onError?.(error instanceof Error ? error.message : 'Stream error');
    } finally {
      clearTimeout(timeoutId);
      this.clearLivenessTimer();
      // Release the reader lock
      if (reader) {
        try {
          reader.releaseLock();
        } catch {
          // Ignore errors during cleanup
        }
      }
      this.abortController = null;

      // Wait for all pending tool operations to complete before returning.
      // Without this, the caller's await resolves while tools are still
      // executing as detached promises — if they fail, onDone never fires
      // and the panel stays stuck in loading state.
      if (this.pendingToolOperations > 0 || this.toolQueue.length > 0) {
        debug('StreamingClient: waiting for', this.pendingToolOperations, 'tool operations +', this.toolQueue.length, 'queued');
        this.toolsCompletePromise = new Promise<void>(resolve => {
          this.toolsCompleteResolver = resolve;
        });
        await this.toolsCompletePromise;
        debug('StreamingClient: all tool operations finished');
      }
    }
  }

  /**
   * Process a single SSE event.
   */
  private processEvent(eventText: string, callbacks: StreamCallbacks): void {
    const lines = eventText.split('\n');
    let eventType = '';
    let eventData = '';

    for (const line of lines) {
      if (line.startsWith('event:')) {
        eventType = line.substring(6).trim();
      } else if (line.startsWith('data:')) {
        eventData = line.substring(5);
        if (eventData.startsWith(' ')) {
          eventData = eventData.substring(1);
        }
      }
    }

    if (!eventType) {
      return;
    }

    switch (eventType) {
      case 'start':
        debug('StreamingClient: received start event, sessionId:', eventData);
        // Track sessionId for tool result submissions
        this.currentSessionId = eventData;
        callbacks.onStart?.(eventData);
        break;

      case 'token':
        const token = eventData.replace(/\\n/g, '\n');
        callbacks.onToken?.(token);
        break;

      case 'block':
        try {
          const block = JSON.parse(eventData) as MessageBlock;
          callbacks.onBlock?.(block);
        } catch (e) {
          console.warn('Failed to parse block:', eventData);
        }
        break;

      case 'done':
        {
          let inputTokens = 0;
          let outputTokens = 0;
          try {
            const usage = JSON.parse(eventData) as { inputTokens: number; outputTokens: number };
            inputTokens = usage.inputTokens;
            outputTokens = usage.outputTokens;
          } catch {
            // leave at 0
          }

          // If tool operations are pending (queued or executing), defer onDone
          // so the StreamingRenderer doesn't set isCompleted=true and drop
          // subsequent tokens/blocks from tool result streams.
          if (this.pendingToolOperations > 0 || this.toolQueue.length > 0) {
            debug('StreamingClient: deferring done until tool operations complete',
              'pending:', this.pendingToolOperations, 'queued:', this.toolQueue.length);
            this.deferredDone = { callbacks, inputTokens, outputTokens };
          } else {
            callbacks.onDone?.(inputTokens, outputTokens);
          }
        }
        break;

      case 'error':
        callbacks.onError?.(eventData);
        break;

      case 'language':
        debug('StreamingClient: backend resolved language:', eventData);
        callbacks.onLanguage?.(eventData);
        break;

      case 'loading':
        debug('StreamingClient: received loading event (more blocks coming)');
        callbacks.onLoading?.();
        break;

      case 'tool_request':
        // MOTHBALLED: Endpoint tool execution disabled for delivery simplification.
        // If received, log warning and ignore.
        console.warn('[PMS] tool_request received but endpoint tools are mothballed');
        break;
    }
  }

  /**
   * Queue a tool request for sequential execution.
   * Tools are processed one at a time to guarantee ordering - critical for
   * comparison mode where results must arrive in the correct sequence.
   */
  // MOTHBALLED: Endpoint tool execution disabled. Prefixed with _ to suppress unused warning.
  private _enqueueToolRequest(request: ToolRequest, callbacks: StreamCallbacks): void {
    this.toolQueue.push({ request, callbacks });
    debug('StreamingClient: tool queued, queue size:', this.toolQueue.length);
    this._processNextTool();
  }

  /**
   * Process the next tool in the queue. Only one tool executes at a time.
   */
  private async _processNextTool(): Promise<void> {
    if (this.isProcessingTool || this.toolQueue.length === 0) return;
    this.isProcessingTool = true;

    const item = this.toolQueue.shift()!;
    const { request: toolRequest, callbacks } = item;
    this.pendingToolOperations++;

    // Show loading indicator while tool executes and result is processed
    callbacks.onLoading?.();

    try {
      if (this.isAborted) {
        this.pendingToolOperations--;
        this.fireDeferredDoneIfReady();
        return;
      }

      const result = await callbacks.onToolRequest!(toolRequest);

      if (this.isAborted) {
        this.pendingToolOperations--;
        this.fireDeferredDoneIfReady();
        return;
      }

      const sessionId = toolRequest.sessionId || this.currentSessionId;
      if (sessionId) {
        await this.sendToolResultAndHandleStream(result, sessionId, callbacks);
      } else {
        this.pendingToolOperations--;
        this.fireDeferredDoneIfReady();
        console.warn('No session ID available for tool result submission');
      }
    } catch (err) {
      if (this.isAborted) {
        this.pendingToolOperations--;
        this.fireDeferredDoneIfReady();
        return;
      }
      console.warn('Tool execution failed:', err);
      const sessionId = toolRequest.sessionId || this.currentSessionId;
      if (sessionId) {
        const errorResult: ToolResult = {
          toolUseId: toolRequest.toolUseId,
          toolName: toolRequest.toolName,
          result: err instanceof Error ? err.message : 'Tool execution failed',
          isError: true,
          errorType: 'execution',
        };
        await this.sendToolResultAndHandleStream(errorResult, sessionId, callbacks);
      } else {
        this.pendingToolOperations--;
        this.fireDeferredDoneIfReady();
      }
    } finally {
      this.isProcessingTool = false;
      // Safety net: ensure deferred done fires even if sendToolResultAndHandleStream
      // missed it (e.g., backend returned error without done event)
      this.fireDeferredDoneIfReady();
      // Process next queued tool
      this._processNextTool();
    }
  }

  /**
   * Send tool execution result back to the orchestrator and handle the SSE response.
   *
   * The backend continues the Claude conversation after receiving the tool result,
   * streaming the response back via SSE. We process this stream using the same
   * callbacks as the original request.
   */
  private async sendToolResultAndHandleStream(
    result: ToolResult,
    sessionId: string,
    callbacks: StreamCallbacks
  ): Promise<void> {
    // Each tool result gets its own abort controller - don't cancel previous ones
    // Multiple tools may be called in sequence (e.g., compare Alice and Ben)
    const toolAbortController = new AbortController();

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Accept': 'text/event-stream',
    };

    if (this.apiKey) {
      headers['X-Api-Key'] = this.apiKey;
    }

    let reader: ReadableStreamDefaultReader<Uint8Array> | null = null;

    try {
      const response = await fetch(
        `${this.baseUrl}/api/sessions/${sessionId}/tool-results`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify(result),
          signal: toolAbortController.signal,
        }
      );

      if (!response.ok) {
        const error = await response.text();
        if (!this.isAborted) {
          callbacks.onError?.(error || `Tool result submission failed: HTTP ${response.status}`);
        }
        return;
      }

      // Process the SSE response stream (Claude's continued response)
      reader = response.body?.getReader() ?? null;
      if (!reader) {
        if (!this.isAborted) {
          callbacks.onError?.('No response body from tool result endpoint');
        }
        return;
      }

      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        // Check if stream was explicitly aborted
        if (this.isAborted) {
          break;
        }

        const { done, value } = await reader.read();

        if (done) {
          break;
        }

        buffer += decoder.decode(value, { stream: true });

        // Process complete events in buffer
        const events = buffer.split('\n\n');
        buffer = events.pop() || '';

        for (const eventText of events) {
          this.processEvent(eventText, callbacks);
        }
      }

      // Process any remaining buffer
      if (buffer.trim() && !this.isAborted) {
        this.processEvent(buffer, callbacks);
      }

    } catch (error) {
      // Ignore abort errors (expected when stream is cancelled)
      if (error instanceof Error && error.name === 'AbortError') {
        return;
      }
      console.warn('Failed to send tool result:', error);
      if (!this.isAborted) {
        callbacks.onError?.(error instanceof Error ? error.message : 'Tool result submission failed');
      }
    } finally {
      // Release the reader lock
      if (reader) {
        try {
          reader.releaseLock();
        } catch {
          // Ignore errors during cleanup
        }
      }
      this.pendingToolAbortController = null;
      this.pendingToolOperations--;
      this.fireDeferredDoneIfReady();
    }
  }

  /**
   * Fire the deferred onDone callback if all tool operations are complete.
   */
  private fireDeferredDoneIfReady(): void {
    if (this.pendingToolOperations <= 0 && this.toolQueue.length === 0) {
      // Fire deferred onDone callback
      if (this.deferredDone) {
        debug('StreamingClient: all tool operations complete, firing deferred done');
        const { callbacks, inputTokens, outputTokens } = this.deferredDone;
        this.deferredDone = null;
        callbacks.onDone?.(inputTokens, outputTokens);
      }
      // Resolve the tools-complete promise so stream() can return
      if (this.toolsCompleteResolver) {
        this.toolsCompleteResolver();
        this.toolsCompleteResolver = null;
        this.toolsCompletePromise = null;
      }
    }
  }

  /**
   * Abort the current stream and any pending tool result streams.
   */
  abort(): void {
    this.isAborted = true;
    this.clearLivenessTimer();
    this.pendingToolOperations = 0;
    this.toolQueue = [];
    this.isProcessingTool = false;
    this.deferredDone = null;

    // Resolve the tools-complete promise so stream() doesn't hang
    if (this.toolsCompleteResolver) {
      this.toolsCompleteResolver();
      this.toolsCompleteResolver = null;
      this.toolsCompletePromise = null;
    }

    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }

    if (this.pendingToolAbortController) {
      this.pendingToolAbortController.abort();
      this.pendingToolAbortController = null;
    }
  }

  /**
   * Check if there are pending tool operations.
   */
  hasPendingToolOperations(): boolean {
    return this.pendingToolOperations > 0;
  }
}

// =============================================================================
// HELPERS
// =============================================================================

/** Tracking/analytics query params to strip from page URLs */
const TRACKING_PARAMS = new Set([
  // Google Analytics / Ads
  'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content',
  'gclid', 'gclsrc', 'dclid', 'gbraid', 'wbraid',
  // Facebook / Meta
  'fbclid', 'fb_action_ids', 'fb_action_types', 'fb_source', 'fb_ref',
  // Microsoft / Bing
  'msclkid',
  // HubSpot
  'hsa_cam', 'hsa_grp', 'hsa_mt', 'hsa_src', 'hsa_ad', 'hsa_acc',
  'hsa_net', 'hsa_ver', 'hsa_la', 'hsa_ol', 'hsa_kw',
  // Mailchimp
  'mc_cid', 'mc_eid',
  // General tracking
  '_ga', '_gl', '_hsenc', '_hsmi', '_openstat',
  'ref', 'referrer',
]);

/**
 * Strip tracking/analytics query params from a URL while keeping
 * meaningful params (e.g., ?category=shoes&page=2).
 * Also removes hash fragments.
 */
function cleanPageUrl(href: string): string {
  try {
    const url = new URL(href);
    const cleaned = new URLSearchParams();
    url.searchParams.forEach((value, key) => {
      if (!TRACKING_PARAMS.has(key.toLowerCase())) {
        cleaned.append(key, value);
      }
    });
    const qs = cleaned.toString();
    return url.origin + url.pathname + (qs ? '?' + qs : '');
  } catch {
    // Fallback: just return origin + pathname
    return window.location.origin + window.location.pathname;
  }
}
